import React, { Component, Fragment } from 'react'
import ReactDOMServer from 'react-dom/server'
import i18n from 'i18next'
import { observer } from 'mobx-react'
import { reaction } from 'mobx'
import AnimateHeight from 'react-animate-height'
import * as R from 'ramda'

import colours from '../Chart/colours'
import Chart from '../Chart'
import OptionResponse from '../OptionResponse'
import ReportPageTitle from '../ReportPageTitle'
import OptionTitle from './OptionTitle'
import { getQuestionTitle, getOptionTitle } from '../../../../../utils/QuestionHelper'
import PanelHeader from '../../../../../UniformComponents/PanelHeader'
import Panel from '../../../../../UniformComponents/Panel'
import Button from '../../../../../UniformComponents/Button'
import ChartSelector from '../ChartSelector'
import DebugInfo from '../DebugInfo'
import QuestionEdit from '../QuestionEdit'
import GroupedRows from './GroupedRows'
import Typography from '../../../../../UniformComponents/Typography'
import AiOverview from './AiOverview'

@observer
class SingleAxis extends Component {
    constructor(props) {
        super(props)
        const {
            question,
            reportFilter,
            reportStore,
            dataKey,
            store,
            pageQuestionId,
        } = props
        const { survey } = store
        this.series = []
        const showOther = new Map()

        this.categories = question.optionSet.options.map((option) => (
            getOptionTitle({
                langs: option.langs,
                locale: reportStore.locale,
                defaultLocale: survey.metadata.defaultLocale,
                initialLocale: question.metadata.initialLocale,
            })
        ))
        question.optionSet.options.forEach((option) => {
            if (option.hasOptionOther) {
                showOther.set(option.id, {
                    label: getOptionTitle({
                        langs: option.langs,
                        locale: reportStore.locale,
                        defaultLocale: survey.metadata.defaultLocale,
                        initialLocale: question.metadata.initialLocale,
                    }),
                    show: false,
                })
            }
        })
        this.chartColours = [
            colours.getSerieColour('survey'),
            colours.getSerieColour('comparison'),
        ]
        Array.from(reportFilter.benchmarks).forEach(([key]) => {
            this.chartColours.push(colours.getSerieColour(key))
        })
        this.mounted = false
        this.legends = {}
        this.isMaxbiReport = survey.isMaxbi()
        this.indexArray = this.isMaxbiReport ? reportStore.filterResponses(dataKey, question)
            : Array.from(Array(question.optionSet.options.length).keys())
        this.showChart = this.isMaxbiReport ? reportStore.showChart(question) : true
        this.state = {
            updated: new Date().getTime(),
            showFilter: false,
            showOther,
            sorting: {
                direction: undefined,
                sortBy: undefined,
                defaultSorting: Array.from(Array(question.optionSet.options.length).keys()),
                sortedIndexArray: this.indexArray,
            },
            responses: {},
            chartType: question.settings.report.graphType,
        }

        reaction(
            () => props.reportStore.reportDataFetched,
            () => {
                const showOther = new Map(
                    Array.from(this.state.showOther)
                        .map(([key, value]) => {
                            value.show = false
                            return [key, value]
                        }),
                )

                this.setState({
                    showOther,
                    updated: props.reportStore.reportDataFetched,
                })
                this.initOtherResponses()
            },
        )
        this.chartType = question.settings.report.graphType
        reportStore.verbatim[pageQuestionId] = this.renderOtherPDF
        reportStore.questionSorting[dataKey] = this.getQuestionSorting
        this.initOtherResponses()
    }

    componentWillUnmount() {
        const { reportStore, pageQuestionId, dataKey } = this.props
        Reflect.deleteProperty(reportStore.verbatim, pageQuestionId)
        Reflect.deleteProperty(reportStore.questionSorting, dataKey)
    }

    initOtherResponses = async () => {
        const { question, reportStore } = this.props
        const { showOther } = this.state

        const otherRequest = Array.from(showOther).map(([optionId]) => (
            reportStore.getTextData(question.id, optionId, question.questionTypeCode)
        ))

        const otherResponses = await Promise.all(otherRequest)

        const otherData = Array.from(showOther).reduce((obj, [optionId, v], index) => {
            obj[optionId] = R.sortBy(R.prop('count'))(R.pathOr([], [index, 'labels'], otherResponses).map((l, i) => ({
                label: l,
                count: otherResponses[index].numberOfResponses[i],
            }))).reverse().filter(({ label }) => label !== '')
            return obj
        }, {})

        this.setState({
            responses: otherData,
        })
    }

    toggleOther = (e) => {
        const stateCopy = { ...this.state }
        const optionId = e.target.getAttribute('id')
        const showOther = new Map(this.state.showOther)
        const showOtherOption = showOther.get(optionId)
        showOtherOption.show = !showOtherOption.show
        stateCopy.showOther = showOther.set(optionId, showOtherOption)

        this.setState(stateCopy)
    }

    chartFilter = (e) => {
        const { pageQuestionId, reportStore, dataKey } = this.props
        const { index } = e.point
        const data = reportStore.reportData.questions[dataKey]

        const option = data.yOptions[index]
        const filterId = `${pageQuestionId}_${option.id}`
        const filterIndex = reportStore.reportFilters.indexOf(filterId)

        if (filterIndex === -1) {
            reportStore.reportFilters.push(filterId)
            reportStore.reloadData()
        }
    }

    renderFilter = (optionId) => {
        const { reportStore, question } = this.props
        const { showQuickFilter, quickFilters } = reportStore
        const ruleKey = `${question.id}_${optionId}`
        const hasFilter = quickFilters.has(ruleKey)
        const className = hasFilter
            ? 'fa-regular fa-square-check'
            : 'fa-regular fa-square'

        if (!showQuickFilter) return null

        return (
            <div className="quick-filter-check-container">
                <i
                    onClick={() => { reportStore.filterByOption(question, optionId) }}
                    className={className}
                />
            </ div>
        )
    }

    colMapper = ({ path, type, colorKey, onClick }, data, option, index) => {
        const {
            question, reportStore, store, reportFilter, dataKey,
        } = this.props
        const { survey } = store
        let col = null
        if (type === 'pct') {
            const label = `${R.pathOr(
                0,
                [index],
            )(
                R.pathOr([], path.split('.'))(data),
            ).toFixed(2)
            }%`
            col = (
                <td
                    style={{ cursor: !R.isNil(onClick) ? 'pointer' : 'auto' }}
                >
                    {label}
                </td>
            )
        } else if (type === 'OptionResponse') {
            col = (
                <td>
                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center', justifyContent: 'center' }}>
                        {this.renderFilter(option.id)}
                        <OptionResponse
                            id={`${dataKey.replace(/_/gi, '|')}_${option.id}`}
                            value={R.pathOr(0, [...path.split('.'), index])(data)}
                            reportStore={reportStore}
                            reportFilter={reportFilter}
                        />
                    </div>
                </td>
            )
        } else if (type === 'optionTitle') {
            col = (
                <th>
                    <OptionTitle
                        id={option.id}
                        reportStore={reportStore}
                        option={option}
                        locale={reportStore.locale}
                        defaultLocale={survey.metadata.defaultLocale}
                        initialLocale={question.metadata.initialLocale}
                        toggleOther={this.toggleOther}
                    />
                </th>
            )
        } else if (type === 'optionSelected') {
            col = (
                <td style={{ textAlign: 'center' }}>
                    <i className="fas fa-check" />
                </td>
            )
        }
        return col
    }

    sortBy = (col) => {
        const sorting = R.clone(this.state.sorting)
        const {
            reportStore, question, store, dataKey,
        } = this.props
        const { survey } = store
        const data = reportStore.reportData.questions[dataKey]
        console.log(`sortBy ${JSON.stringify(col)}`)

        const oldSortBy = sorting.sortBy

        let values = []
        if (!col.path) {
            sorting.sortBy = 'label'
            values = question.optionSet.options.reduce((newOptions, o) => {
                newOptions.push(getOptionTitle({
                    langs: o.langs,
                    locale: reportStore.locale,
                    defaultLocale: survey.metadata.defaultLocale,
                    initialLocale: question.metadata.initialLocale,
                }))
                return newOptions
            }, [])
        } else {
            sorting.sortBy = col.path
            values = R.pathOr(0, col.path.split('.'))(data)
        }
        const sortedIndexArray = Array
            .from(Array(values.length).keys())
            .sort((a, b) => (values[a] < values[b] ? -1 : values[a] > values[b] ? 1 : 0))

        if (sorting.sortBy !== oldSortBy || !sorting.direction) {
            sorting.direction = 'ASC'
        } else {
            sorting.direction = sorting.direction === 'ASC'
                ? 'DESC'
                : undefined
        }

        if (sorting.direction === 'ASC') {
            sorting.sortedIndexArray = sortedIndexArray
        } else if (sorting.direction === 'DESC') {
            sorting.sortedIndexArray = sortedIndexArray.reverse()
        } else {
            sorting.sortedIndexArray = sorting.defaultSorting
        }

        this.setState({
            sorting,
        })
    }

    sortIcon = ({ path }) => {
        const { sorting } = this.state
        const { direction, sortBy } = sorting
        let icon = <span style={{ display: 'inline' }} />
        if (
            direction
            && (
                !path && sortBy === 'label'
                || path === sortBy
            )
        ) {
            const iconClass = direction === 'ASC'
                ? 'fal fa-long-arrow-up'
                : 'fal fa-long-arrow-down'
            icon = <i style={{ marginLeft: '5px', width: '6px', color: '#81859B' }} className={`${iconClass}`} />
        }

        return icon
    }

    getChartCategorySet = () => {
        const { sorting } = this.state
        const { sortedIndexArray } = sorting
        const { question } = this.props

        return sortedIndexArray.map((sortedIndex) => question.optionSet.options[sortedIndex])
    }

    getChartCategories = () => {
        const { question, reportStore, store } = this.props
        const { survey } = store
        const { sorting } = this.state
        const { sortedIndexArray } = sorting

        const categories = sortedIndexArray.map((sortedIndex) => (
            getOptionTitle({
                langs: question.optionSet.options[sortedIndex].langs,
                locale: reportStore.locale,
                defaultLocale: survey.metadata.defaultLocale,
                initialLocale: question.metadata.initialLocale,
            })
        ))

        return categories
    }

    legendClick = ({ index }) => {
        const key = Object.keys(this.legends)[index]
        this.legends[key] = !this.legends[key]
    }

    renderOther = (key, value) => (
        <div
            className="reportQuestionOthertext"
            style={{
                border: '1px solid #d4ccb0',
                background: '#fafaf4',
                padding: '.5em .5em .5em .7em',
            }}
        >
            <h3 className="reportquestionOthertitle" style={{ fontSize: '1em' }}>
                {value.label}
                {' '}
                answers
            </h3>
            {this.state.responses[key]
                ? this.state.responses[key].map(({ label, count }, i) => (
                    <div className="textResponse" data-index={i}>
                        {label}

                        <span>
                            (
                            {count}
                            )
                        </span>
                    </div>
                )) : null}
        </div>
    )

    chartTypeChanged = (chartType) => {
        this.chartType = chartType
    }

    getQuestionSorting = () => {
        const { sorting } = this.state
        return [sorting.sortedIndexArray, this.chartType]
    }

    renderOtherPDF = () => {
        const { showOther } = this.state

        const otherJS = Array.from(showOther).map(([key, value]) => (
            this.renderOther(key, value)
        ))

        return ReactDOMServer.renderToStaticMarkup(otherJS)
    }

    renderRows = (cols, filterRows) => {
        const { sorting } = this.state
        const { question, reportStore, dataKey } = this.props
        const data = reportStore.reportData.questions[dataKey]

        return (
            sorting.sortedIndexArray.map((index) => {
                const option = question.optionSet.options[index]
                return !filterRows || filterRows.has(option.id)
                    ? (
                        <tr className="col" key={`option_${option.id}`}>
                            {(
                                cols
                                    .filter((col) => !col.hide)
                                    .map((col) => (
                                        this.colMapper(col, data, option, index)
                                    ))
                            )}
                            <td className="tlast" />
                        </tr>
                    ) : null
            })
        )
    }

    renderGroupedRows = (cols) => {
        const { store, question, reportStore } = this.props
        return (
            <GroupedRows
                question={question}
                renderRows={this.renderRows}
                cols={cols}
                reportStore={reportStore}
                store={store}
            />
        )
    }

    generateDataTable = () => {
        const {
            question,
            reportStore,
            dataKey,
            pageQuestionId,
        } = this.props
        const data = reportStore.reportData.questions[dataKey]

        const benchmarkCols = question.metadata.isBenchmarked
            ? Array.from(reportStore.benchmarks2)
                .map(([key, { value, label }]) => ({
                    label,
                    path: `benchmarks.${key}`,
                    key,
                    type: 'pct',
                    colorKey: key,
                    hide: !(value && R.hasPath(['benchmarks', key], data)),
                    noData: R.isNil(R.pathOr(null, ['benchmarks', key], data)),
                    chartHide: Object.hasOwnProperty.call(this.legends, key)
                        ? !this.legends[key]
                        : !['company', 'client', 'industry'].includes(key),
                    hideInLegend: !(value && R.hasPath(['benchmarks', key], data)),
                }))
            : []
        const comparisonCols = Array.from(reportStore.yoyComparisons)
            .filter(([id]) => (R.pathOr(false, ['comparison', id.toUpperCase()])(data)))
            .map(([id, { label }]) => ({
                label: reportStore.comparisonTitle(label),
                path: `comparison.${id.toUpperCase()}`,
                type: 'pct',
                colorKey: 'comparison',
                key: `survey_${label}`,
            }))

        const cols = this.isMaxbiReport ? [
            {
                label: i18n.t('report_option_name'),
                class: 'tleft',
                type: 'optionTitle',
            },
            {
                label: i18n.t('report_question_selected'),
                class: 'tright',
                path: 'numberOfResponses',
                type: 'optionSelected',
            },
        ] : [
            {
                label: i18n.t('report_option_name'),
                class: 'tleft',
                type: 'optionTitle',
            },
            {
                label: i18n.t('report_question_responses'),
                path: 'numberOfResponses',
                type: 'OptionResponse',
            },
            {
                label: reportStore.surveyTitle(),
                path: 'pctOfResponses',
                type: 'pct',
                colorKey: 'survey',
                chartHide: Object.hasOwnProperty.call(this.legends, 'survey')
                    ? !this.legends.survey
                    : false,
                key: 'survey',
            },
            ...comparisonCols,
            ...benchmarkCols,
        ]

        this.cols = cols.slice(2)

        if (Object.keys(this.legends).length === 0) {
            this.cols.forEach((col) => {
                this.legends[col.key] = !col.hide
            })
        }

        const benhmarkDefault = () => 0.00

        return (
            <div style={{ overflowX: 'auto' }}>
                <table
                    className="datatable question tablesorter tablesorter-default"
                    style={{
                        width: '100%',
                    }}
                >
                    <thead>
                        <tr className="col tablesorter-headerRow">
                            {cols
                                .filter((col) => !col.hide)
                                .map((col) => (
                                    this.isMaxbiReport
                                        ? (
                                            <th className={`${col.class || ''} tablesorter-header`}>
                                                <div className="tablesorter-header-inner">
                                                    {col.label}
                                                </div>
                                            </th>
                                        ) : (
                                            <th style={{ cursor: 'pointer' }} className={`${col.class || ''} tablesorter-header`} onClick={() => this.sortBy(col)}>
                                                <div className="tablesorter-header-inner">
                                                    {col.label}
                                                    {this.sortIcon(col)}
                                                </div>
                                            </th>
                                        )
                                ))}
                            <td className="tlast" />
                        </tr>
                    </thead>
                    <tbody>
                        {(question.hasHeaderGroups
                            ? this.renderGroupedRows(cols)
                            : this.renderRows(cols)
                        )}
                    </tbody>
                    {!this.isMaxbiReport && (
                        <tfoot>
                            {this.renderSampleSizeRow(data, cols, question)}
                            {question.optionSet.hasNumericCodes && (
                                <tr className="col">
                                    <th>
                                        {i18n.t('report_question_average')}
                                        :
                                    </th>
                                    <td />
                                    <td className="stats">
                                        {R.pathOr(0, ['aggregate', 'average'], data).toFixed(2)}
                                    </td>
                                    {
                                        Array.from(reportStore.yoyComparisons).map(([id]) => (
                                            R.hasPath(['comparison', id.toUpperCase()], data)
                                                ? (
                                                    <td className="stats">
                                                        {R.or(Number(R.pathOr(0, ['aggregate', 'comparison', id.toUpperCase()], data)), 0).toFixed(2)}
                                                    </td>
                                                ) : null
                                        ))
                                    }
                                    {question.metadata.isBenchmarked && (
                                        Array.from(reportStore.benchmarks2).map(([key, { value }]) => (
                                            value
                                                && R.hasPath(['aggregate', 'benchmarks', key], data)
                                                ? (
                                                    <td className="stats">
                                                        {R.either(R.path(['aggregate', 'benchmarks', key]), benhmarkDefault)(data).toFixed(2)}
                                                    </td>
                                                ) : null
                                        ))
                                    )}
                                    <td className="tlast" />
                                </tr>
                            )}
                            {question.questionTypeCode === 'CH' && (
                                <tr className="col">
                                    <th>
                                        {i18n.t('report_average_num_responses')}
                                        :
                                    </th>
                                    <td />
                                    <td className="stats">
                                        {R.pathOr(0, ['aggregate', 'userAverage'], data).toFixed(2)}
                                    </td>
                                    {
                                        Array.from(reportStore.yoyComparisons).map(([id]) => (
                                            R.hasPath(['comparison', id.toUpperCase()], data)
                                                ? (
                                                    <td className="stats">
                                                        {R.pathOr(0, ['aggregate', 'avgResponses', id.toUpperCase()], data).toFixed(2)}
                                                    </td>
                                                ) : null
                                        ))
                                    }
                                    {question.isBenchmarked && (
                                        Array.from(reportStore.benchmarks2).map(([key, { value }]) => (
                                            value && data.benchmarks[key]
                                                ? <td />
                                                : null
                                        ))
                                    )}
                                    <td className="tlast" />
                                </tr>
                            )}
                            {question.metadata.kpiType === 'NPS' && question.optionSet.hasNumericCodes && (
                                <tr className="col">
                                    <th>
                                        {i18n.t('report_net_promoter_score')}
                                        :
                                    </th>
                                    <td />
                                    <td className="stats">
                                        {R.pathOr(0, ['aggregate', 'nps', 'survey'], data).toFixed(1)}
                                    </td>
                                    {
                                        Array.from(reportStore.yoyComparisons).map(([id]) => (
                                            R.hasPath(['comparison', id.toUpperCase()], data)
                                                ? (
                                                    <td className="stats">
                                                        {R.pathOr(0, ['aggregate', 'nps', id.toUpperCase()], data).toFixed(1)}
                                                    </td>
                                                ) : null
                                        ))
                                    }
                                    {Array.from(reportStore.benchmarks2).map(([key, { value }]) => (
                                        value && R.hasPath(['benchmarks', key], data)
                                            ? (
                                                <td className="stats">
                                                    {R.pathOr(0, ['aggregate', 'nps', key], data).toFixed(1)}
                                                </td>
                                            ) : null
                                    ))}
                                    <td className="tlast" />
                                </tr>
                            )}
                        </tfoot>
                    )}
                </table>
            </div>
        )
    }

    renderSampleSizeRow = (questionData, cols, question) => {
        if (!['CH', 'RD'].includes(question.questionTypeCode)) return null
        const leftEmptyCols = Array.from({ length: 1 })

        const uniqueResp = questionData?.uniqueResponses
        const currentSampleSize = uniqueResp <= 0 ? 0 : (uniqueResp ?? i18n.t('gen_not_applicable'))
        const sampleSizeCols = [currentSampleSize] // current year

        const comparisonKeys = cols // other years
            .filter((c) => c.path && c.path.startsWith('comparison.'))
            .map((c) => c.path.split('.')[1])
        comparisonKeys.forEach((ck) => sampleSizeCols.push(questionData.comparisonTotals[ck]))
        const benchmarkCols = cols.filter((c) => c.path && c.path.startsWith('benchmarks.') && !c.hide)

        return (
            <tr className="col">
                <th>
                    {i18n.t('report.sampleSize')}
                </th>
                {leftEmptyCols.map((_) => (<td style={{ cursor: 'auto' }} />))}
                {sampleSizeCols.map((num) => (
                    <td>
                        {`(${num})`}
                    </td>
                ))}
                {benchmarkCols.map((_) => (
                    <td style={{ cursor: 'auto' }}>
                        {`(${i18n.t('gen_not_applicable')})`}
                    </td>
                ))}
                <td className="tlast" />
            </tr>
        )
    }

    npsScore = () => {
        const { question, reportStore, dataKey } = this.props
        const data = reportStore.reportData.questions[dataKey]
        let npsClass = question.metadata.kpiType === 'NPS' ? 'npColorOne' : ''
        let npsScore = -100
        if (question.metadata.kpiType === 'NPS') {
            npsScore = R.pathOr(0, ['aggregate', 'nps', 'survey'], data)
            if (npsScore >= 0) {
                npsClass += ' npColorOne '
            } else if (npsScore >= -51 && npsScore <= -1) {
                npsClass += ' npColorTwo '
            } else if (npsScore <= -50) {
                npsClass += ' npColorThree '
            }
        }
        const showKpi = question.metadata.kpiType && question.optionSet.hasNumericCodes

        if (!showKpi) return null

        return (
            <div>
                <Panel cssClass="kpi stats">
                    <div className="iconCircle">
                        <i className="fa-light fa-gauge" />
                    </div>
                    <div className="details">
                        <label>{i18n.t('report.badge.npsScore')}</label>
                        <span>{(question.metadata.kpiType === 'NPS') ? npsScore.toFixed(0) : R.pathOr(0, ['aggregate', 'average'], data).toFixed(2)}</span>
                    </div>
                </Panel>
            </div>
        )
    }

    aiBadge = () => {
        const { store, question } = this.props
        let el
        if (store.surveyAiQuestionMapping?.size < 1) return el
        const questionId = question.id.toLowerCase()
        if ([...store.surveyAiQuestionMapping?.values()].includes(questionId)) {
            el = (
                <div>
                    <Panel cssClass="kpi stats">
                        <div className="iconCircle">
                            <i className="fa-light fa-sparkles" />
                        </div>
                        <div className="details">
                            <p>{i18n.t('report.badge.aiBadge')}</p>
                        </div>
                    </Panel>
                </div>
            )
        }
        return el
    }

    chartSelector = () => {
        const { chartType } = this.state
        return (
            <div>
                <ChartSelector
                    chartType={chartType}
                    onChange={(chartType) => this.setState({ chartType })}
                />
            </div>
        )
    }

    downloadButton = () => {
        const { reportStore, dataKey } = this.props
        return (
            <div
                className="questionFooter"
            >
                <Button
                    cssClass="primary regular"
                    label={i18n.t('report_export_options')}
                    icon="fa-light fa-folder-arrow-down"
                    onClick={() => reportStore.downloadQuestionExcel(dataKey)}
                />
            </div>
        )
    }

    render() {
        const {
            question,
            store,
            pageQuestionId,
            reportStore,
            dataKey,
            reportFilter,
            pageId,
        } = this.props
        const { survey } = store
        const data = R.or(reportStore.reportData.questions[dataKey], {})
        return (
            (question.questionTypeCode !== 'ST') ? (
                <Fragment>
                    <Panel cssClass="question">
                        <PanelHeader>
                            <div id={`questionTitle_${question.pageQuestionId}`} className="pageTitleContainer">
                                <ReportPageTitle
                                    h2={getQuestionTitle({
                                        content: question.content.shortTitle,
                                        formContent: R.pathOr(null, ['formContent', 'shortTitle'])(question),
                                        locale: reportStore.locale,
                                        defaultLocale: survey.metadata.defaultLocale,
                                        initialLocale: question.metadata.initialLocale,
                                    })}
                                    h3={getQuestionTitle({
                                        content: question.content.longTitle,
                                        locale: reportStore.locale,
                                        defaultLocale: survey.metadata.defaultLocale,
                                        initialLocale: question.metadata.initialLocale,
                                    })}
                                    reportStore={reportStore}
                                    questionTypeCode={question.questionTypeCode}
                                />
                                <QuestionEdit reportStore={reportStore} pageId={pageId} />
                                {this.npsScore()}
                                {this.aiBadge()}
                            </div>
                        </PanelHeader>
                        <DebugInfo
                            store={store}
                            questionTypeCode={question.questionTypeCode}
                            reportStore={reportStore}
                            questionId={question.id}
                            pageQuestionId={pageQuestionId}
                        />
                        {['RD', 'CH', 'SB'].includes(question.questionTypeCode) && <AiOverview />}
                        <div id={`questionData_${pageQuestionId}`}>
                            {['RD', 'CH', 'SB'].includes(question.questionTypeCode) && this.generateDataTable()}
                        </div>
                        {Array.from(this.state.showOther).map(([key, value]) => (
                            <AnimateHeight
                                duration={250}
                                height={value.show ? 'auto' : 0}
                            >
                                {this.renderOther(key, value)}
                            </AnimateHeight>
                        ))}
                        {this.showChart && data.hasResponses && (
                            <Fragment>
                                {this.chartSelector()}
                                <Chart
                                    colors={this.chartColours}
                                    survey={survey}
                                    dataKey={dataKey}
                                    pointEvent={this.chartFilter}
                                    question={question}
                                    reportFilter={reportFilter}
                                    data={data}
                                    series={this.cols}
                                    sort={`${this.state.sorting.sortBy}_${this.state.sorting.direction}`}
                                    sortedIndexArray={this.state.sorting.sortedIndexArray}
                                    isBenchmarked={question.metadata.isBenchmarked}
                                    pageQuestionId={question.pageQuestionId}
                                    id={question.pageQuestionId}
                                    categorySet={this.getChartCategorySet()}
                                    chartType={this.state.chartType}
                                    questionTypeCode={question.questionTypeCode}
                                    kpiType={question.metadata.kpiType}
                                    categories={this.getChartCategories()}
                                    locale={reportStore.locale}
                                    reportStore={reportStore}
                                    percentages
                                />
                                {this.downloadButton()}
                            </Fragment>
                        )}
                    </Panel>
                </Fragment>
            ) : null
        )
    }
}

export default SingleAxis
